import * as React from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, Plus, Upload } from 'lucide-react';
import { api, ApiError, qk } from '@client/lib/api';
import { useDatasets, type Dataset } from '@client/lib/dataset-api';
import { PageHeader } from '@client/components/admin/shared';
import { Button } from '@client/components/ui/button';
import { Dialog } from '@client/components/ui/overlay';
import { Field, Input, Select } from '@client/components/ui/input';
import { Spinner, EmptyState } from '@client/components/ui/feedback';
import { Badge } from '@client/components/ui/badge';
import { Table, Td, Th, Tr } from '@client/components/ui/table';
import { useToast } from '@client/components/ui/toast';
import { fmtRelative } from '@client/lib/format';

export const Route = createFileRoute('/admin/datasets/')({ component: DatasetsPage });

function DatasetsPage() {
  const datasets = useDatasets();
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <PageHeader title="Datasets" eyebrow="xlsx / csv → dashboards" actions={<Button onClick={() => setOpen(true)}><Plus />New dataset</Button>} />
      <div className="px-6 py-6 md:px-8">
        {datasets.isPending ? <Spinner /> : datasets.data && datasets.data.datasets.length === 0 ? (
          <EmptyState icon={Database} title="No datasets yet" body="Create a dataset, then upload an xlsx or csv version. Dashboards bind to a version and are published as snapshots." action={{ label: 'New dataset', onClick: () => setOpen(true) }} />
        ) : (
          <div className="rounded-card border border-border bg-card">
            <Table>
              <thead><tr><Th>Dataset</Th><Th>Currency</Th><Th>Latest version</Th><Th>Status</Th><Th align="right">Rows</Th><Th>Updated</Th></tr></thead>
              <tbody>
                {datasets.data?.datasets.map((d) => (
                  <Tr key={d.id}>
                    <Td><Link to="/admin/datasets/$id" params={{ id: d.id }} className="font-[520]">{d.name}</Link></Td>
                    <Td>{d.currency}</Td>
                    <Td>{d.latest_version ? `v${d.latest_version.versionNo} · ${d.latest_version.filename}` : <span className="text-muted">none</span>}</Td>
                    <Td>{d.latest_version ? <Badge tone={d.latest_version.parseStatus === 'ready' ? 'good' : d.latest_version.parseStatus === 'failed' ? 'critical' : 'warning'}>{d.latest_version.parseStatus}</Badge> : '—'}</Td>
                    <Td align="right">{d.latest_version?.rowCount ?? '—'}</Td>
                    <Td className="text-muted">{fmtRelative(d.updatedAt, 'en')}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
      {open ? <NewDatasetDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function NewDatasetDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = React.useState({ name: '', currency: 'EUR', timezone: 'Europe/Rome', csv_locale: 'en' });
  const create = useMutation({
    mutationFn: () => api<{ dataset: Dataset }>('/api/admin/datasets', { body: form }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: qk.admin('datasets') }); onClose(); navigate({ to: '/admin/datasets/$id', params: { id: res.dataset.id } }); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not create', body: e instanceof ApiError ? e.code : String(e) }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="New dataset" description="Curate the data in your spreadsheet, then upload it here. The uploaded file is the source of truth." footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => create.mutate()} disabled={!form.name.trim()} loading={create.isPending}>Create</Button></>}>
      <Field label="Name" htmlFor="ds-name"><Input id="ds-name" autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Annual costs (Qonto)" /></Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Currency" htmlFor="ds-cur"><Input id="ds-cur" value={form.currency} maxLength={3} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></Field>
        <Field label="Timezone" htmlFor="ds-tz"><Input id="ds-tz" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></Field>
        <Field label="CSV number format" htmlFor="ds-loc" hint="1.234,56 vs 1,234.56"><Select id="ds-loc" value={form.csv_locale} onChange={(e) => setForm({ ...form, csv_locale: e.target.value })}><option value="en">1,234.56 (EN)</option><option value="it">1.234,56 (IT)</option></Select></Field>
      </div>
    </Dialog>
  );
}
